import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Outlet, createRootRoute } from '@tanstack/react-router';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Shell } from '@/components/app/shell';
import { api, ApiError } from '@/lib/api';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30 * 1000,
			refetchOnWindowFocus: false,
			retry: (failureCount, error) => {
				if (error instanceof ApiError && [401, 403, 404].includes(error.status)) return false;
				return failureCount < 2;
			},
		},
	},
});

function RootContent() {
	const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });

	if (meQuery.isLoading) {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-muted/20">
				<img src="/docr-logo-400.png" alt="DocR" className="h-9 w-auto select-none" draggable={false} />
				<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (meQuery.isError || !meQuery.data) {
		const status = meQuery.error instanceof ApiError ? meQuery.error.status : null;
		const noAccess = status === 401 || status === 403;
		return (
			<div className="flex min-h-screen items-center justify-center bg-muted/20 p-6">
				<div className="w-full max-w-md rounded-xl border bg-background p-8 shadow-sm">
					<div className="flex flex-col items-center gap-3 text-center">
						<img src="/docr-logo-400.png" alt="DocR" className="h-9 w-auto select-none" draggable={false} />
						<div className="mt-2 flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
							<ShieldAlert className="h-5 w-5" />
						</div>
						<h1 className="text-lg font-semibold">{noAccess ? 'Geen toegang' : 'Even niet bereikbaar'}</h1>
						<p className="max-w-xs text-sm text-muted-foreground">
							{noAccess
								? 'Je e-mailadres is niet gekoppeld aan een DocR-account, of je sessie is verlopen. Vraag een beheerder om toegang of probeer opnieuw in te loggen.'
								: 'Wij konden de DocR-server niet bereiken. Controleer je verbinding en probeer het opnieuw.'}
						</p>
						<div className="mt-3 flex gap-2">
							<Button size="sm" variant="outline" onClick={() => window.location.reload()}>
								Opnieuw proberen
							</Button>
							{noAccess && (
								<Button size="sm" asChild>
									<a href="/cdn-cgi/access/logout">Uitloggen</a>
								</Button>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<Shell user={meQuery.data}>
			<Outlet />
		</Shell>
	);
}

export const Route = createRootRoute({
	component: () => (
		<QueryClientProvider client={queryClient}>
			<RootContent />
		</QueryClientProvider>
	),
});
